import { type RealtimeIdentity } from '@doxyme/realtime';
import { Vendor } from '../../webrtc/media';
import type { ConnectionTestDataItem, ConnectionTestResponse } from './types';
import { type ConnectionTestAbstractVendor } from './connectionTestVendor/connectionTestAbstractVendor';
import {
  statsReportToObject,
  getRoundTripTime,
  getPacketLoss,
  getResultFromTestData,
} from './testResults';
import { getConnectionTestVendor } from './connectionTestVendor';

export const STATS_INTERVAL = 1000;

export class ConnectionTest {
  private statsInterval?: NodeJS.Timeout;

  private testVendor?: ConnectionTestAbstractVendor;

  private testData: ConnectionTestDataItem[] = [];

  /**
   * Actual start, which includes vendor initialization, vendor start, stats collecting interval
   * @param identity - Realtime identity
   * @param vendor - Vendor to use for connection test
   * @param constraints - Media constraints
   * @param constraints.audio - Audio constraints
   * @param constraints.video - Video constraints
   */
  async start(
    identity: RealtimeIdentity,
    vendor = Vendor.Vonage,
    { audio = true, video = true }: MediaStreamConstraints = {},
  ): Promise<void> {
    if (this.isRunning()) {
      throw new Error('Connection test is already running');
    }

    this.testVendor = await getConnectionTestVendor(vendor);

    await this.testVendor.start(identity, { audio, video });

    this.startCollectingStats();
  }

  /**
   * Stop vendor and stats collecting
   */
  async stop(): Promise<void> {
    this.stopCollectingStats();

    return this.testVendor?.stop();
  }

  /**
   * Get current test stats
   */
  getStats(): ConnectionTestResponse {
    return {
      stats: [...this.testData],
      result: getResultFromTestData(this.testData),
    };
  }

  /**
   *  Check if test is running
   */
  isRunning(): boolean {
    return Boolean(this.statsInterval);
  }

  /**
   * Start interval to get WebRTC stats and convert them to our internal test data.
   */
  private startCollectingStats(): void {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.statsInterval = setInterval(async () => {
      const statsReport = await this.testVendor?.getStats();

      if (statsReport === undefined) {
        return;
      }

      const stats = Object.values(statsReportToObject(statsReport));

      this.testData.push({
        videoPacketLoss: getPacketLoss(stats, 'video'),
        audioPacketLoss: getPacketLoss(stats, 'audio'),
        roundTripTime: getRoundTripTime(stats),
      });
    }, STATS_INTERVAL);
  }

  /**
   * Stop interval of getting WebRTC stats. Clear previous test data.
   */
  private stopCollectingStats(): void {
    this.testData = [];

    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = undefined;
    }
  }
}
